import { useEffect, useRef } from "react";

/**
 * Live audio waveform visualisation via Web Audio API.
 * Shows 32 frequency bars in a horizontal strip that react to mic amplitude.
 * Pass a MediaStream (mic) while active; will start/stop with the stream.
 */
const VoiceWaveform = ({ stream, active = true, color = "#1FCB4F", bars = 32, height = 56 }) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!stream || !active) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const audioCtx = new AudioCtx();
    ctxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    analyserRef.current = analyser;

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();

    const draw = () => {
      analyser.getByteFrequencyData(buffer);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      const barWidth = (w - (bars - 1) * 3) / bars;
      const step = Math.floor(buffer.length / bars);

      for (let i = 0; i < bars; i++) {
        // Average a few neighbouring frequency bins for smoother bars
        let sum = 0;
        for (let j = 0; j < step; j++) sum += buffer[i * step + j] || 0;
        const v = (sum / step) / 255; // 0..1
        const minH = 3;
        const barH = Math.max(minH, v * h);
        const x = i * (barWidth + 3);
        const y = (h - barH) / 2;
        ctx.fillStyle = color;
        // rounded bar
        const r = Math.min(barWidth / 2, 4);
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + barWidth - r, y);
        ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + r);
        ctx.lineTo(x + barWidth, y + barH - r);
        ctx.quadraticCurveTo(x + barWidth, y + barH, x + barWidth - r, y + barH);
        ctx.lineTo(x + r, y + barH);
        ctx.quadraticCurveTo(x, y + barH, x, y + barH - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { source.disconnect(); } catch {}
      try { analyser.disconnect(); } catch {}
      if (audioCtx.state !== "closed") {
        audioCtx.close().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, active, color, bars]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="voice-waveform"
      style={{
        width: "100%",
        height,
        display: "block",
        background: "transparent",
      }}
    />
  );
};

export default VoiceWaveform;
